import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

/** Normalized review status, mirrored from the backend. */
export type ReviewStatus =
  | 'APPROVED'
  | 'CHANGES_REQUESTED'
  | 'REVIEW_REQUIRED';

/** A GitHub label attached to a pull request. */
export interface PullRequestLabel {
  name: string;
  /** Hex colour without the leading `#`. */
  color: string;
}

/** The PR's author — used to render an avatar in review-requested lists. */
export interface PullRequestAuthor {
  login: string;
  avatarUrl: string;
}

/** A single open pull request authored by the current user. */
export interface OpenPullRequest {
  repo: string;
  repoUrl: string;
  title: string;
  url: string;
  number: number;
  createdAt: string;
  reviewStatus: ReviewStatus;
  labels: PullRequestLabel[];
  /** Author of the PR. `undefined` if GitHub didn't return author data. */
  author?: PullRequestAuthor;
}

export type ReviewView = 'personal' | 'teams';

export interface ReviewRequestedResponse {
  prs: OpenPullRequest[];
  /** True when the backend truncated results to the display cap (20 items). */
  capped: boolean;
}

export interface GithubMyPrsApi {
  /** Fetch the current user's open PRs across all tracked GitHub orgs. */
  getOpenPullRequests(): Promise<OpenPullRequest[]>;
  /**
   * Fetch PRs where the current user (or their teams) is a requested reviewer.
   * `view=personal` → direct review requests; `view=teams` → team requests.
   */
  getReviewRequestedPullRequests(
    view: ReviewView,
  ): Promise<ReviewRequestedResponse>;
}

export const githubMyPrsApiRef = createApiRef<GithubMyPrsApi>({
  id: 'plugin.github-my-prs.service',
});

export class GithubMyPrsClient implements GithubMyPrsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async getOpenPullRequests(): Promise<OpenPullRequest[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('github-my-prs');
    const response = await this.fetchApi.fetch(`${baseUrl}/open`);

    if (!response.ok) {
      // Surface the backend's `{ error }` message when present; fall back to
      // the HTTP status otherwise.
      let message = `Failed to load open pull requests (${response.status} ${response.statusText})`;
      try {
        const body = await response.json();
        if (typeof body?.error === 'string' && body.error) {
          message = body.error;
        }
      } catch {
        // Non-JSON error body — keep the status-based message.
      }
      throw new Error(message);
    }

    const data = (await response.json()) as { prs?: OpenPullRequest[] };
    return data.prs ?? [];
  }

  async getReviewRequestedPullRequests(
    view: ReviewView,
  ): Promise<ReviewRequestedResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('github-my-prs');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/review-requested?view=${view}`,
    );

    if (!response.ok) {
      let message = `Failed to load review-requested pull requests (${response.status} ${response.statusText})`;
      try {
        const body = await response.json();
        if (typeof body?.error === 'string' && body.error) {
          message = body.error;
        }
      } catch {
        // Non-JSON error body — keep the status-based message.
      }
      throw new Error(message);
    }

    const data = (await response.json()) as ReviewRequestedResponse;
    return { prs: data.prs ?? [], capped: data.capped ?? false };
  }
}
